import classNames from "classnames";
import moment from "moment";
import AppContext from "PFApp/app_context";
import { useBookingModuleContext } from "PFApp/booking/parts/providers/booking_module_context_provider";
import InlineRangeCalendar from "PFComponents/calendar/inline_range_calendar";
import TooltipIcon from "PFComponents/tooltip_icon/tooltip_icon";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import InfoIconSvg from "PFIcons/info.svg";
import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { WorkforceCalendarData } from "../context/booking_overview_context.types";
import { useBookingRowsContext } from "../context/booking_rows_context";
import { useBookingsOverviewContext } from "../context/bookings_overview_context";
import css from "./calendar_range.module.scss";

const InfoIcon = (props) => <InfoIconSvg {...props} />;

interface CalendarRangeProps {
  disabled: boolean;
  tooltipDisabled: boolean;
}

const CalendarRange = ({ disabled, tooltipDisabled }: CalendarRangeProps) => {
  const {
    showUtilizationMetrics,
    calendarData: {
      data: { loading: workforceLoading }
    }
  } = useBookingsOverviewContext<WorkforceCalendarData>();
  const { calendarRange, setCalendarRange } = useBookingModuleContext();
  const { rowsLoadingByKey } = useBookingRowsContext();
  const areSomeRowsLoading = Object.values(rowsLoadingByKey).some((loading) => loading);

  const {
    store: { currentAccount }
  } = useContext(AppContext);

  const { formatISODate } = useDateFormatter();
  const { t } = useTranslation("bookingModule");
  const [maxDate, setMaxDate] = useState<string>();
  const maxCalculatedMonthsLimit = useMemo(
    () => formatISODate(moment().add(currentAccount.availability?.calculated_months_limit, "months")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentAccount.availability?.calculated_months_limit]
  );

  const handleStartDateChange = (start: string) => {
    if (moment(start).add(1, "year").isAfter(moment(maxCalculatedMonthsLimit))) {
      setMaxDate(maxCalculatedMonthsLimit);
    } else {
      setMaxDate(formatISODate(moment(start).add(1, "year")));
    }
  };

  const handleCalendarRangeChange = (start: string, end: string) =>
    setCalendarRange({
      min: start,
      max: end
    });

  const tooltipContent = showUtilizationMetrics
    ? t("workforce.calendarRangeTooltipInfo")
    : t("workforce.calendarRangeTooltipInfoUtilizationHidden");

  return (
    <div className={css.calendarRangeContainer}>
      {/* @ts-ignore */}
      <InlineRangeCalendar
        startDate={calendarRange.min}
        endDate={calendarRange.max}
        minDate={formatISODate()}
        maxDate={maxDate ?? maxCalculatedMonthsLimit}
        displayMonthValue={calendarRange.min}
        disabled={disabled || workforceLoading || areSomeRowsLoading}
        handleChange={handleCalendarRangeChange}
        popperOptions={{
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                padding: 5
              }
            }
          ]
        }}
        popperClassName={css.popper}
        className={css.calendarRoot}
        inputFieldSetProps={{
          inputContainerClassName: css.inputContainer,
          inputClassName: css.input,
          iconClassName: css.icon
        }}
        onStartDateChange={handleStartDateChange}
      />
      <TooltipIcon
        className={classNames(css.tooltipIcon, { [css.tooltipIconDisabled]: tooltipDisabled })}
        content={tooltipContent}
        IconComponent={InfoIcon}
        width={16}
        height={16}
        tooltipProps={{
          disabled: tooltipDisabled
        }}
      />
    </div>
  );
};

export default CalendarRange;
